import clsx from 'clsx';
import { z } from 'zod';

import styles from './OrderedList.module.scss';

export const ORDERED_LIST_MAX_CARDS = 12;
export const ORDERED_LIST_MAX_META_ITEMS = 8;

export const orderedListCardSchema = z
  .object({
    title: z.string().min(1),
    body: z.string().min(1),
    meta: z.array(z.string().min(1)).max(ORDERED_LIST_MAX_META_ITEMS),
  })
  .strict();

export const orderedListPropsSchema = z
  .object({
    cards: z.array(orderedListCardSchema).min(1).max(ORDERED_LIST_MAX_CARDS),
  })
  .strict();

export type OrderedListCard = z.infer<typeof orderedListCardSchema>;
export type OrderedListDataProps = z.infer<typeof orderedListPropsSchema>;

export interface OrderedListProps extends OrderedListDataProps {
  /** Extra class forwarded to the root `<ol>`. */
  className?: string;
}

/**
 * Numbered editorial list for ranked takeaways, findings, or recommendations.
 */
export const OrderedList = ({ cards, className }: OrderedListProps) => (
  <ol className={clsx(styles.root, className)}>
    {cards.map((card, index) => (
      <li key={`${card.title}-${index}`} className={styles.card}>
        <span className={styles.index} aria-hidden="true">
          {String(index + 1).padStart(2, '0')}
        </span>

        <div className={styles.content}>
          <div className={styles.textBlock}>
            <p className={styles.title}>{card.title}</p>
            <p className={styles.body}>{card.body}</p>
          </div>

          {card.meta.length > 0 && (
            <p className={styles.meta}>
              {card.meta.map((metaItem, metaIndex) => (
                <span
                  key={`${metaItem}-${metaIndex}`}
                  className={styles.metaItem}
                >
                  {metaItem}
                </span>
              ))}
            </p>
          )}
        </div>
      </li>
    ))}
  </ol>
);
